import { TASK_COLUMNS, TASK_COLUMN_LABELS, type TaskColumn } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient, type QueryFilters } from '@tanstack/react-query'
import { Fragment, useMemo, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '../components/Icon.js'
import {
  Badge,
  Button,
  ErrorState,
  Input,
  Page,
  PageTitle,
  Select,
  SkeletonRows,
} from '../components/ui.js'
import { useBandDrag } from '../lib/band-drag.js'
import { cn } from '../lib/cn.js'
import { formatDate } from '../lib/format.js'
import { useJobHref } from '../lib/job-overlay.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'

/**
 * The office board: four columns of everything the office owes somebody.
 *
 * The cards are `Task` rows, the same model the job card's follow-ups use — an
 * office task is one with nothing in `jobId` (rule 20). So a follow-up raised
 * in a subfloor arrives in To do without anybody copying it across, and the
 * card that carries it links back to the job it came from.
 *
 * **Done is a column and not a lane.** A finished task is one with
 * `completedAt`, which the job card has always read; a fourth stored lane
 * beside it would be two answers to one question and they would disagree the
 * first time either was written alone. Dropping a card in Done stamps the
 * date, dragging it out clears it and the card returns to the lane it left,
 * because the lane it was in is still true. Decision 74.
 *
 * The gesture is `useBandDrag`, as the people list and the catalog are: a
 * column is a band because it carries `data-band`, a card is a `data-row`
 * inside it, and the pointer's own `pointerover` picks the target rather than
 * anything measuring rectangles. Three things it inherits and needs:
 *
 * - The **whole card** is the grab. A grip you have to aim at reads as no drag.
 * - The move is written into the cache **before the server answers**, so the
 *   card lands under the hand instead of springing back for a round trip.
 * - A card's own click asks `drag.dragged()` first, or the release that ended a
 *   move opens whatever was just dropped.
 *
 * Touch is left to the browser, again as the other two lists are — a finger
 * dragging a card is a finger scrolling the page. The lane `<select>` on each
 * card is the path that works on a phone, with a keyboard, and in a screen
 * reader, and it is not decoration for the mouse gesture.
 */

type Task = RouterOutputs['tasks']['list'][number]

/** Where a task is drawn, which is Done before it is any lane. */
function columnOf(task: Task): TaskColumn {
  return task.completedAt === null ? task.lane : 'DONE'
}

export function Tasks() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const jobHref = useJobHref()

  const [assignee, setAssignee] = useState('')
  const [title, setTitle] = useState('')

  const tasks = useQuery(trpc.tasks.list.queryOptions({}))
  const people = useQuery(trpc.tasks.assignable.queryOptions())

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.tasks.pathFilter())
    // A job's follow-up list is the same rows, so a tick here has to reach it.
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const create = useMutation(trpc.tasks.create.mutationOptions({ onSuccess: invalidate }))
  const update = useMutation(trpc.tasks.update.mutationOptions({ onSuccess: invalidate }))
  const remove = useMutation(trpc.tasks.remove.mutationOptions({ onSuccess: invalidate }))

  /**
   * A move, with the board corrected before the server answers.
   *
   * Without it the card springs back to the column it was picked up from,
   * waits a round trip and then jumps, which reads as the drag having failed
   * and then being undone by somebody else. The snapshot goes back if the
   * write is refused, and either way the query is invalidated — the server is
   * the one that knows what was allowed.
   */
  const listFilter = trpc.tasks.list.queryFilter() as QueryFilters

  const move = useMutation(
    trpc.tasks.move.mutationOptions({
      onMutate: ({ column, orderedIds }) => {
        const snapshot = queryClient.getQueriesData<Task[]>(listFilter)
        const at = new Map(orderedIds.map((id, index) => [id, index]))
        const now = new Date()

        for (const [key, data] of snapshot) {
          if (!data) continue
          queryClient.setQueryData(
            key,
            data.map((task) => {
              const index = at.get(task.id)
              if (index === undefined) return task

              return column === 'DONE'
                ? { ...task, sortOrder: index, completedAt: task.completedAt ?? now }
                : { ...task, sortOrder: index, lane: column, completedAt: null }
            }),
          )
        }

        return snapshot
      },
      onError: (_error, _input, snapshot) => {
        for (const [key, data] of snapshot ?? []) queryClient.setQueryData(key, data)
      },
      onSettled: invalidate,
    }),
  )

  const rows = tasks.data ?? []

  const shown = useMemo(
    () => (assignee ? rows.filter((task) => task.assignee?.id === assignee) : rows),
    [rows, assignee],
  )

  /** The four columns, each already in the order the office put them in. */
  const columns = useMemo(() => {
    const byColumn = new Map<TaskColumn, Task[]>(TASK_COLUMNS.map((key) => [key, []]))
    for (const task of shown) byColumn.get(columnOf(task))?.push(task)
    for (const list of byColumn.values()) list.sort((a, b) => a.sortOrder - b.sortOrder)
    return byColumn
  }, [shown])

  const drag = useBandDrag({
    enabled: true,
    onDrop: (id, band, before) => {
      const column = band as TaskColumn
      if (!TASK_COLUMNS.includes(column)) return
      if (before === id) return

      const members = (columns.get(column) ?? []).map((task) => task.id)
      const without = members.filter((member) => member !== id)
      // An insertion point that is not in this column — the empty strip does
      // this — means the end of it.
      const at = before && without.includes(before) ? without.indexOf(before) : without.length
      const orderedIds = [...without.slice(0, at), id, ...without.slice(at)]

      const carried = rows.find((task) => task.id === id)
      const wasHere = carried && columnOf(carried) === column
      if (wasHere && orderedIds.every((value, index) => value === members[index])) return

      move.mutate({ column, orderedIds })
    },
  })

  const carried = drag.carrying ? rows.find((task) => task.id === drag.carrying?.id) : undefined

  const add = () => {
    const trimmed = title.trim()
    if (!trimmed) return

    create.mutate({ title: trimmed, ...(assignee ? { assigneeId: assignee } : {}) })
    setTitle('')
  }

  const error = tasks.error ?? create.error ?? update.error ?? remove.error ?? move.error

  return (
    <Page field>
      <PageTitle>Tasks</PageTitle>

      <p className="-mt-1 text-xs text-ink-500">
        Office work, and the follow-ups raised on jobs. Drag a card between columns, or use the
        picker on it.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={title}
          placeholder="Something to do"
          aria-label="New task"
          className="min-w-48 max-w-[420px] flex-1"
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') add()
          }}
        />
        <Button variant="secondary" loading={create.isPending} onClick={add}>
          Add
        </Button>

        <label className="ml-auto flex items-center gap-1.5 text-xs text-ink-600">
          Assigned to
          <Select
            value={assignee}
            aria-label="Assigned to"
            className="w-40"
            onChange={(event) => setAssignee(event.target.value)}
          >
            <option value="">Anyone</option>
            {(people.data ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {error ? <ErrorState message={errorMessage(error)} /> : null}

      {tasks.isPending ? (
        <SkeletonRows rows={6} />
      ) : (
        <div className="grid gap-[var(--stack-gap)] md:grid-cols-2 xl:grid-cols-4">
          {TASK_COLUMNS.map((column) => (
            <Column
              key={column}
              column={column}
              tasks={columns.get(column) ?? []}
              people={people.data ?? []}
              drag={drag}
              jobHref={jobHref}
              onLane={(id, next) =>
                move.mutate({ column: next, orderedIds: [id] })
              }
              onAssign={(id, assigneeId) => update.mutate({ id, assigneeId })}
              onRemove={(id) => remove.mutate({ id })}
            />
          ))}
        </div>
      )}

      {carried ? <CarriedCard ref={drag.ghostRef} task={carried} landing={drag.over} /> : null}
    </Page>
  )
}

/* -------------------------------------------------------------------------- */

function Column({
  column,
  tasks,
  people,
  drag,
  jobHref,
  onLane,
  onAssign,
  onRemove,
}: {
  column: TaskColumn
  tasks: Task[]
  people: { id: string; name: string }[]
  drag: ReturnType<typeof useBandDrag>
  jobHref: (jobId: string) => ReturnType<ReturnType<typeof useJobHref>>
  onLane: (id: string, column: TaskColumn) => void
  onAssign: (id: string, assigneeId: string | null) => void
  onRemove: (id: string) => void
}) {
  const over = drag.over === column
  const done = column === 'DONE'

  const caret = (beforeId: string | null) =>
    over && drag.before === beforeId && drag.carrying?.id !== beforeId ? (
      <li aria-hidden className="relative h-0">
        <span className="absolute inset-x-1 -top-px h-0.5 rounded-full bg-brand-600" />
      </li>
    ) : null

  return (
    <section
      data-band={column}
      aria-label={TASK_COLUMN_LABELS[column]}
      className={cn(
        'board-card flex min-h-40 flex-col bg-white',
        over && 'ring-2 ring-brand-500',
      )}
    >
      <div className="flex items-center gap-2 border-b border-ink-200 px-3 py-2">
        <h2 className="font-display text-[15px] leading-snug font-bold text-brand-800">
          {TASK_COLUMN_LABELS[column]}
        </h2>
        <span className="text-xs tabular-nums text-ink-400">{tasks.length}</span>
      </div>

      {tasks.length === 0 ? (
        <p
          className={cn(
            'px-3 py-6 text-center text-xs text-ink-400',
            drag.carrying && 'm-2 rounded-lg border border-dashed border-ink-300',
            over && 'border-brand-500 text-brand-700',
          )}
        >
          {drag.carrying ? 'Drop to move here' : done ? 'Nothing finished lately.' : 'Nothing here.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5 p-1.5">
          {tasks.map((task) => (
            <Fragment key={task.id}>
              {caret(task.id)}
              <TaskCard
                task={task}
                column={column}
                people={people}
                jobHref={jobHref}
                lifted={drag.carrying?.id === task.id}
                onGrab={(event) => drag.begin(event, task.id, column)}
                onLane={(next) => onLane(task.id, next)}
                onAssign={(assigneeId) => onAssign(task.id, assigneeId)}
                onRemove={() => onRemove(task.id)}
              />
            </Fragment>
          ))}
          {caret(null)}
        </ul>
      )}
    </section>
  )
}

function TaskCard({
  task,
  column,
  people,
  jobHref,
  lifted,
  onGrab,
  onLane,
  onAssign,
  onRemove,
}: {
  task: Task
  column: TaskColumn
  people: { id: string; name: string }[]
  jobHref: (jobId: string) => ReturnType<ReturnType<typeof useJobHref>>
  lifted: boolean
  onGrab: (event: ReactPointerEvent) => void
  onLane: (column: TaskColumn) => void
  onAssign: (assigneeId: string | null) => void
  onRemove: () => void
}) {
  const overdue =
    task.dueAt !== null && task.completedAt === null && new Date(task.dueAt) < new Date()

  return (
    <li
      data-row={task.id}
      onPointerDown={onGrab}
      className={cn(
        'flex cursor-grab flex-col gap-1.5 rounded-[10px] bg-white p-2 ring-1 ring-ink-200',
        lifted && 'opacity-40',
      )}
    >
      <p
        className={cn(
          'text-[13px] leading-snug text-ink-900',
          column === 'DONE' && 'text-ink-500 line-through',
        )}
      >
        {task.title}
      </p>

      {/* The job it came from, when it came from one. `useJobHref` because a
          job has no page: it opens as `?jobId=` over this screen. Rule 13. */}
      {task.job ? (
        <Link
          to={jobHref(task.job.id)}
          data-no-drag
          className="truncate text-[11px] text-ink-500 hover:text-brand-700 hover:underline"
        >
          {task.job.displayNumber ?? `#${task.job.number}`} · {task.job.client.name}
        </Link>
      ) : null}

      {task.dueAt ? (
        <Badge tone={overdue ? 'red' : 'neutral'}>
          {overdue ? 'Overdue ' : 'Due '}
          {formatDate(task.dueAt)}
        </Badge>
      ) : null}

      <div className="flex items-center gap-1">
        {/* The keyboard, screen reader and phone path onto a column. Drag is
            the shortcut for a mouse and never the only way. */}
        <label data-no-drag className="min-w-0 flex-1">
          <span className="sr-only">{`Column for ${task.title}`}</span>
          <select
            aria-label={`Column for ${task.title}`}
            value={column}
            onChange={(event) => onLane(event.target.value as TaskColumn)}
            className="h-[var(--control-sm)] w-full rounded-md border-0 bg-transparent px-1 text-[11px] text-ink-500 hover:bg-ink-50 hover:text-ink-900 focus:ring-2 focus:ring-brand-600"
          >
            {TASK_COLUMNS.map((key) => (
              <option key={key} value={key}>
                {TASK_COLUMN_LABELS[key]}
              </option>
            ))}
          </select>
        </label>

        <label data-no-drag className="min-w-0 flex-1">
          <span className="sr-only">{`Assignee for ${task.title}`}</span>
          <select
            aria-label={`Assignee for ${task.title}`}
            value={task.assignee?.id ?? ''}
            onChange={(event) => onAssign(event.target.value || null)}
            className="h-[var(--control-sm)] w-full rounded-md border-0 bg-transparent px-1 text-[11px] text-ink-500 hover:bg-ink-50 hover:text-ink-900 focus:ring-2 focus:ring-brand-600"
          >
            <option value="">Nobody</option>
            {people.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </select>
        </label>

        <Button
          size="sm"
          variant="ghost"
          data-no-drag
          aria-label={`Remove ${task.title}`}
          onClick={onRemove}
        >
          <Icon name="trash" className="size-3.5" />
        </Button>
      </div>
    </li>
  )
}

/**
 * The card under the pointer during a drag.
 *
 * Fixed to the viewport and positioned by the gesture rather than by React, so
 * a pointermove is a style write and not the whole board reconciling. Nothing
 * in it is hit-testable: it sits under the cursor, and a column has to be able
 * to see the pointer arrive.
 */
function CarriedCard({
  ref,
  task,
  landing,
}: {
  ref: React.Ref<HTMLDivElement>
  task: Task
  landing: string | null
}) {
  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none fixed top-0 left-0 z-50 flex max-w-[260px] items-center gap-2 rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs shadow-lg"
    >
      <span className="truncate font-medium text-ink-900">{task.title}</span>
      {landing && landing !== columnOf(task) ? (
        <span className="shrink-0 text-ink-500">
          → {TASK_COLUMN_LABELS[landing as TaskColumn]}
        </span>
      ) : null}
    </div>
  )
}